/* eslint jsx-a11y/click-events-have-key-events: 1 */
/* eslint jsx-a11y/no-static-element-interactions: 1 */

import PropTypes from 'prop-types';
import React, { Component } from 'react';
import background from 'flowplayer-background';
import Button from '@fenderdigital/ui/Button';
import { FormattedMessage } from 'react-intl';
import { isMobile } from 'react-device-detect';
import { withRouter } from 'react-router-dom';
import messages from './messages';
import VideoLowerThird from '../VideoLowerThird/VideoLowerThird';
import VideoPlayerBasic from './VideoPlayerBasic';
import VideoPlayerEvents from './VideoPlayerEvents';
import VideoPoster from './VideoPoster';
import { getLessonAttrs, getThumbnailConfig } from './video-player-utils';
import { isDesktop } from '../../routes/lesson/components/lesson-utils';
import { getVideoSource } from '../../utils/curriculum-utils';
import { videoPlaybackEvents } from './VideoPlayerConstants';
import { trackInteractionEvent, parsePlayer, trackSpeedChange } from './VideoPlayerAnalytics';
import { updateLessonProgress, updateLessonStandaloneProgress } from '../../actions/my-progress';
import '../../vendor/flowplayer/styles/flowplayer.css';
import '../../vendor/flowplayer/styles/flowplayer.background.css';
import './styles/main.scss';
import { getFlag } from '../../lib/feature-flags';
/*!
     FORKED AND CHANGED
     flowplayer.min.js was modified from the original v7.0.4
     to include change for Android detection.
     CHANGED
     (\d\.\d) became (\d+(\.\d+)?)
     IF THIS FILE IS UPDATED IT MUST BE TO VERSION 7.2.7 or later
  */

let flowplayer = null;
class VideoPlayer extends Component {
  static renderBasicLessonVideo(lesson, startCallBack, endCallback, isTabFullScreen) {
    const { id, type, instrument, activityLessonVideo } = getLessonAttrs(lesson);
    if (activityLessonVideo && activityLessonVideo.mp4720p) {
      const videoURL = activityLessonVideo.mp4720p;
      const { duration, poster } = activityLessonVideo;
      const onStart = () => {
        updateLessonStandaloneProgress(id, type);
        updateLessonProgress(id, type, 0, instrument, duration);
        if (startCallBack) {
          startCallBack();
        }
      };
      const onEnded = () => {
        updateLessonStandaloneProgress(id, type);
        updateLessonProgress(id, type, duration, instrument, duration);
        if (endCallback) {
          endCallback();
        }
      };
      const videoProps = { onStart, onEnded, poster, videoURL };
      return <VideoPlayerBasic {...videoProps} shouldPause={isTabFullScreen} hidePoster />;
    }
    return <div id="video-error" style={{ height: '400px' }} />;
  }

  constructor(props) {
    super(props);
    this.state = {
      error: null,
      finish: false,
      isRestarted: false,
      started: props.autoplay,
    };
    this.getConfiguration = this.getConfiguration.bind(this);
    this.handlePlayerClick = this.handlePlayerClick.bind(this);
    this.handlePosterClick = this.handlePosterClick.bind(this);
  }

  componentDidMount() {
    const { speedTrackData, autoplay } = this.props;
    if (!flowplayer) {
      Promise.all([
        import(/* webpackChunkName: "videoPlayer" */ 'flowplayer-hlsjs'),
        import(/* webpackChunkName: "videoPlayer" */ '../../vendor/flowplayer'),
        import(
          /* webpackChunkName: "videoPlayer" */ '../../vendor/flowplayer/flowplayer.thumbnails.min.js'
        ),
        import(/* webpackChunkName: "videoPlayer" */ '../../vendor/flowplayer/flowplayer.speed.js'),
      ]).then(([engine, fp, fpt, speed]) => {
        flowplayer = fp.default;
        background(flowplayer);
        engine.default(flowplayer);
        fpt.default(flowplayer);
        speed.default(flowplayer, trackSpeedChange(speedTrackData));
        this.setupPlayer(flowplayer);
      });
    } else {
      this.setupPlayer(flowplayer);
    }
  }

  componentDidUpdate(prevProps) {
    const { shouldPause } = this.props;
    if (shouldPause) {
      this.player.pause();
    }
  }

  componentWillUnmount() {
    this.videoPlayerEvents.unregister();
    if (this.player) {
      this.player.conf.keyboard = false;
      this.player.off(videoPlaybackEvents.CUEPOINT);
      this.player.off(videoPlaybackEvents.SEEK);
      this.player.off(videoPlaybackEvents.PROGRESS);
      this.player.off(videoPlaybackEvents.RESUME);
      this.player.shutdown();
    }
  }

  handleCuepoints() {
    // Trigger callback for cuepoints
    this.player.on(videoPlaybackEvents.CUEPOINT, (e, api, cuepoint) => {
      this.props.onStartOfCuepoint(cuepoint);
    });

    this.player.on(videoPlaybackEvents.SEEK, (e, api) => {
      if (api.video.cuepoints) {
        let startOfCuepoint = null;
        api.video.cuepoints.forEach(cuepoint => {
          if (api.video.time >= cuepoint.time) {
            startOfCuepoint = cuepoint;
          }
        });
        this.props.onStartOfCuepoint(startOfCuepoint);
      }
    });

    // Seek to chapter
    if (this.props.startAtTime) {
      if (this.player.playing) {
        this.player.seek(this.props.startAtTime);
      } else {
        this.player.on(videoPlaybackEvents.RESUME, () => {
          // this flag need to be here, so when a user chooses to restart the video
          // via the resume / restart ui, it starts from 0
          const { isRestarted } = this.state;
          if (!isRestarted && isDesktop()) this.player.seek(this.props.startAtTime);
        });
      }
    }
  }

  handlePlayerClick(event) {
    const { player } = this;
    const { paused } = player || {};
    const { trackData, onIsUserBusy } = this.props;
    trackInteractionEvent(event, player, trackData);
    if (paused) {
      onIsUserBusy(paused);
    }
  }

  handlePosterClick() {
    const { onIsUserBusy } = this.props;
    if (this.player) {
      this.player.play();
    }
    this.setState({ started: true });
    onIsUserBusy(true);
  }

  setupPlayer(flowplayer) {
    const { trackData } = this.props;

    this.player = flowplayer(this.videoNode, this.getConfiguration());
    this.videoPlayerEvents = new VideoPlayerEvents(
      this.player,
      this.updateEventState.bind(this),
      trackData,
    );
    this.videoPlayerEvents.register();
    this.handleCuepoints();
  }

  getConfiguration() {
    const {
      background: bg,
      clip,
      duration,
      muted,
      loop,
      poster,
      ratio,
      thumbnail,
      title,
      volume,
      autoplay,
    } = this.props;
    const configuration = {
      adaptiveRatio: true,
      autoplay,
      background: bg ? { audio: !muted } : false,
      brand: 'fender',
      generate_cuepoints: true,
      hlsjs: {
        listeners: ['hls', 'MediaAttached', 'hlsLevelSwitch', 'hlsError', 'hlsManifestParsed'],
        recoverMediaError: true,
        recoverNetworkError: false,
        autoStartLoad: true,
        startLevel: 3,
        capLevelToPlayerSize: true,
      },
      key: process.env.REACT_APP_CLIENT_FLOWPLAYER_KEY,
      loop,
      muted,
      speeds: [0.5, 0.75, 1, 1.25, 1.5],
      mouseoutTimeout: 3000,
      native_fullscreen: true,
      poster,
      ratio,
      title,
      volume,
    };
    configuration.clip = clip;
    if (clip.cuepoints) {
      configuration.clip.cuepoints = clip.cuepoints.map(cuepoint => ({
        time: cuepoint.time,
        title: cuepoint.title,
      }));
    }
    if (thumbnail && duration) {
      const {
        interval: thumbnailSampleInterval,
        rate: thumbnailSampleRate,
        padding: thumbnailPadding,
      } = getThumbnailConfig(thumbnail, duration);
      configuration.thumbnails = {
        interval: thumbnailSampleInterval,
        preload: false,
        template: thumbnail.replace('{count}', '{time}'),
        time_format: t => {
          const count = t % thumbnailSampleRate;
          return (
            thumbnailPadding.substring(0, thumbnailPadding.length - count.toString().length) + count
          );
        },
      };
    }
    return configuration;
  }

  updateEventState(event, value = true, seconds) {
    if (!this || !this.player) {
      return;
    }
    try {
      this.setState({
        [event]: value,
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[VideoPlayer.updateEventState] ', error);
    }
    if (this.props.onEvent) {
      if (event === videoPlaybackEvents.WATCHING) {
        this.props.onWatchedVideoProgress(value, seconds);
      } else {
        this.props.onEvent(event, this.player);
      }
    }
  }

  seekToTime(event, time = 10) {
    event.preventDefault();
    if (this.player) {
      this.player.seek(this.player.video.time + time);
    }
  }

  renderOverlay() {
    const { lowerThird } = this.props;
    return (
      <div>
        {this.player && lowerThird ? (
          <VideoLowerThird lowerThird={lowerThird} player={this.player} />
        ) : null}
      </div>
    );
  }

  renderEndscreen() {
    if (
      process.env.REACT_APP_BROWSER &&
      this.player &&
      this.player.isFullscreen &&
      window.outerWidth < 768
    ) {
      this.player.fullscreen();
    }
    if (this.state.finish && this.props.endscreen) {
      return this.props.endscreen;
    }
    return null;
  }

  renderBackTen() {
    return (
      <div className="skip-ten">
        <button
          type="button"
          onClick={e => {
            this.seekToTime(e, -10);
          }}
          className="skip-ten__button skip-ten__button--back"
        />
      </div>
    );
  }

  renderForwardTen() {
    return (
      <div className="skip-ten z-1">
        <button
          type="button"
          onClick={e => {
            this.seekToTime(e, 10);
          }}
          className="skip-ten__button skip-ten__button--forward"
        />
      </div>
    );
  }

  renderError() {
    const { poster, history } = this.props;
    if (this.state.error) {
      return (
        <div className="error">
          <img alt="poster" className="error__poster" src={`${poster}`} />
          <div className="error__content">
            <p data-test-id="error-title" className="error__title f6">
              <FormattedMessage {...messages.playerErrorTitle} />
            </p>
            <p data-test-id="error-subtitle" className="error__subtitle f6">
              {this.state.error}
            </p>
            <Button
              onClick={() => {
                history.goBack();
              }}
            >
              <FormattedMessage {...messages.playerErrorCTA} />
            </Button>
          </div>
        </div>
      );
    }
    return null;
  }

  renderPoster() {
    const { poster } = this.props;
    const { started } = this.state;
    if (!poster || started === true) return null;
    return <VideoPoster onClick={this.handlePosterClick} poster={poster} />;
  }

  renderRepositionControls() {
    const { renderRepositionControls } = this.props;
    // parsing the player for analytics data, like:
    // framerate, postion, and session_id
    const player = parsePlayer(this.player);
    return renderRepositionControls(player);
  }

  render() {
    const { shouldPause, fullScreenStyles, poster, autoplay } = this.props;
    if (this.player && this.player.error) {
      this.player.shutdown();
      this.videoPlayerEvents.unregister();
      return (
        <VideoPlayerBasic
          poster={poster}
          videoURL={getVideoSource(this.props.clip)}
          shouldPause={shouldPause}
        />
      );
    }
    if (this.state.finish && this.player.isFullscreen) this.player.fullscreen();
    const videoStyle = {
      marginBottom: `${this.props.background ? '-1rem' : null}`,
    };
    // temporary
    /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
    return (
      <div className="video-player h-100">
        {!isMobile && !autoplay && this.renderPoster()}
        <div
          data-video-player
          ref={node => {
            this.videoNode = node;
          }}
          onClick={this.handlePlayerClick}
          className="video-player fp-edgy"
          style={{ ...videoStyle, ...fullScreenStyles }}
        >
          <div className={`${this.state.started ? '' : 'is-paused'}`}>
            {this.renderError()}
            {this.renderForwardTen()}
            {this.renderBackTen()}
            {this.renderOverlay()}
            {this.renderRepositionControls()}
            <div className="gradient" />
          </div>
        </div>
      </div>
    );
  }
}

VideoPlayer.propTypes = {
  history: PropTypes.shape({ push: PropTypes.func.isRequired, replace: PropTypes.func.isRequired })
    .isRequired,
  autoplay: PropTypes.bool,
  background: PropTypes.bool,
  chords: PropTypes.arrayOf(PropTypes.shape({})),
  clip: PropTypes.shape({
    cuepoints: PropTypes.arrayOf(
      PropTypes.shape({
        time: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
      }),
    ),
    sources: PropTypes.arrayOf(
      PropTypes.shape({
        src: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  duration: PropTypes.number,
  endscreen: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  fullScreenStyles: PropTypes.shape({}),
  isResumingFrom: PropTypes.bool,
  onIsUserBusy: PropTypes.func,
  loop: PropTypes.bool,
  lowerThird: PropTypes.arrayOf(PropTypes.shape({})),
  muted: PropTypes.bool,
  onEvent: PropTypes.func,
  onStartOfCuepoint: PropTypes.func,
  onWatchedVideoProgress: PropTypes.func,
  pause: PropTypes.bool,
  poster: PropTypes.string,
  ratio: PropTypes.number,
  renderRepositionControls: PropTypes.func,
  shouldPause: PropTypes.bool,
  speedTrackData: PropTypes.shape({}),
  startAtTime: PropTypes.number,
  thumbnail: PropTypes.string,
  title: PropTypes.string,
  trackData: PropTypes.shape({}),
  volume: PropTypes.number,
};

VideoPlayer.defaultProps = {
  autoplay: false,
  background: false,
  chords: null,
  duration: null,
  endscreen: null,
  fullScreenStyles: null,
  isResumingFrom: null,
  loop: false,
  lowerThird: null,
  muted: false,
  onEvent: null,
  onStartOfCuepoint: () => {},
  onWatchedVideoProgress: () => {},
  onIsUserBusy: () => {},
  pause: false,
  poster: null,
  ratio: 9 / 16,
  renderRepositionControls: () => {},
  shouldPause: false,
  speedTrackData: null,
  startAtTime: null,
  thumbnail: null,
  title: '',
  trackData: null,
  volume: 1,
};

export default withRouter(VideoPlayer);
